import equal from 'fast-deep-equal';
import { DATE_FORMAT } from 'common/constants';
import useFeatureFlags from 'common/hooks/useFeatureFlags';
import usePersistWidgetOptions from 'common/hooks/usePersistWidgetOptions';
import { KwelloBarChartOptions, KwelloLineChartOptions } from 'common/models/chart.model';
import { Feature } from 'common/models/page-config.models';
import { GroupList } from 'common/models/widget.model';
import { RootState } from 'common/store';
import { selectWidgetOptionsById } from 'common/store/slices/widget-config.slice';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

const FAVOUR_WIDGET_BAR_CHART_CONFIG_VALUES: (keyof KwelloBarChartOptions)[] = [
  'categoryLabelFormatting',
];

export interface WidgetOptions {
  groupBy?: string;
  kwelloBarChartOptions?: KwelloBarChartOptions;
  kwelloLineChartOptions?: KwelloLineChartOptions;
  allIncluded?: boolean;
  activeTabId?: number;
}

export interface WidgetOptionsContext extends WidgetOptions {
  updateGroupBy: (value: string) => void;
  updateActiveTabId: (tabId: number) => void;
  toggleAllIncluded: () => void;
  updateKwelloBarChartOptions: (options: KwelloBarChartOptions) => void;
  updateKwelloLineChartOptions: (options: KwelloLineChartOptions) => void;
  resetWidgetOptions: () => void;
  storedOptionsKey?: number;
  isDirty: boolean;
}

interface WidgetOptionsProviderProps {
  pageName: string;
  widgetId: string;
  children?: React.ReactNode;
  showBarChartOptions: boolean;
  barChartOptions?: Partial<KwelloBarChartOptions>;
  showLineChartOptions: boolean;
  lineChartOptions?: Partial<KwelloLineChartOptions>;
  groupOptions: GroupList[];
}

export const DEFAULT_BARCHART_OPTIONS: KwelloBarChartOptions = {
  direction: 'horizontal',
  stack: 'normal',
  seriesValueDisplayType: 'total',
  valueType: 'percentage',
};

export const DEFAULT_LINECHART_OPTIONS: KwelloLineChartOptions = {
  chartType: 'line',
  lineStyle: 'smooth',
  markers: false,
  maxLabels: 20,
  dateFormat: DATE_FORMAT,
};

const WidgetOptions = createContext<WidgetOptionsContext>({
  groupBy: undefined,
  activeTabId: undefined,
  kwelloBarChartOptions: DEFAULT_BARCHART_OPTIONS,
  kwelloLineChartOptions: DEFAULT_LINECHART_OPTIONS,
  updateGroupBy: () => {},
  toggleAllIncluded: () => {},
  updateKwelloBarChartOptions: () => {},
  updateKwelloLineChartOptions: () => {},
  resetWidgetOptions: () => {},
  updateActiveTabId: () => {},
  allIncluded: false,
  isDirty: false,
});

const WidgetOptionsProvider = (props: WidgetOptionsProviderProps) => {
  const {
    pageName,
    widgetId,
    showBarChartOptions,
    showLineChartOptions,
    barChartOptions = {},
    lineChartOptions = {},
    groupOptions = [],
  } = props;
  const { projectId = '' } = useParams();
  const { hasFeature } = useFeatureFlags();
  const hasBarChartOptions = hasFeature(Feature.BarChartOptions) && showBarChartOptions;
  const hasLineChartOptions = hasFeature(Feature.LineChartOptions) && showLineChartOptions;
  const compiledWidgetId = `${pageName}-${widgetId}`;
  const prevProjectId = useRef<string | null>(projectId);

  const storedWidgetOptionsItem = useSelector((state: RootState) =>
    selectWidgetOptionsById(state, projectId, compiledWidgetId)
  );

  const { key: optionsKey } = storedWidgetOptionsItem ?? {};

  const [localWidgetOptions, setLocalWidgetOptions] = useState<WidgetOptions>({
    groupBy: storedWidgetOptionsItem?.value.groupBy ?? groupOptions[0]?.memberName,
    kwelloBarChartOptions: {
      ...DEFAULT_BARCHART_OPTIONS,
      ...barChartOptions,
      ...(hasBarChartOptions ? storedWidgetOptionsItem?.value.kwelloBarChartOptions ?? {} : {}),
      ...FAVOUR_WIDGET_BAR_CHART_CONFIG_VALUES.reduce(
        (acc: Partial<KwelloBarChartOptions>, key: keyof Partial<KwelloBarChartOptions>) => {
          if (barChartOptions[key] === undefined) {
            return acc;
          }

          acc[key] = barChartOptions[key] as any;
          return acc;
        },
        {}
      ),
    },
    kwelloLineChartOptions: {
      ...DEFAULT_LINECHART_OPTIONS,
      ...lineChartOptions,
      ...(hasLineChartOptions ? storedWidgetOptionsItem?.value.kwelloLineChartOptions ?? {} : {}),
    },
    allIncluded: storedWidgetOptionsItem?.value.allIncluded ?? false,
    activeTabId: storedWidgetOptionsItem?.value.activeTabId,
  });

  useEffect(() => {
    if (prevProjectId.current !== projectId) {
      setLocalWidgetOptions({
        groupBy: storedWidgetOptionsItem?.value.groupBy,
        kwelloBarChartOptions: {
          ...DEFAULT_BARCHART_OPTIONS,
          ...barChartOptions,
          ...(hasBarChartOptions ? storedWidgetOptionsItem?.value.kwelloBarChartOptions ?? {} : {}),
        },
        kwelloLineChartOptions: {
          ...DEFAULT_LINECHART_OPTIONS,
          ...lineChartOptions,
          ...(hasLineChartOptions
            ? storedWidgetOptionsItem?.value.kwelloLineChartOptions ?? {}
            : {}),
        },
      });
      prevProjectId.current = projectId;
    }
  }, [
    barChartOptions,
    hasBarChartOptions,
    hasLineChartOptions,
    lineChartOptions,
    projectId,
    storedWidgetOptionsItem?.value.groupBy,
    storedWidgetOptionsItem?.value.kwelloBarChartOptions,
    storedWidgetOptionsItem?.value.kwelloLineChartOptions,
  ]);

  const { saveOptions, updateOptions, deleteOptions } = usePersistWidgetOptions({
    id: compiledWidgetId,
  });

  const updateWidgetOptions = useCallback(
    (widgetOptions: WidgetOptions) => {
      const newOptions = {
        ...localWidgetOptions,
        ...widgetOptions,
      };

      setLocalWidgetOptions(newOptions);

      if (optionsKey) {
        void updateOptions({ key: optionsKey, widgetOptions: newOptions });
      } else {
        void saveOptions(newOptions);
      }
    },
    [localWidgetOptions, optionsKey, saveOptions, updateOptions]
  );

  const updateGroupBy = useCallback(
    (value: string) => {
      updateWidgetOptions({ groupBy: value });
    },
    [updateWidgetOptions]
  );

  const updateActiveTabId = useCallback(
    (tabId: number) => {
      updateWidgetOptions({ activeTabId: tabId });
    },
    [updateWidgetOptions]
  );

  const updateKwelloBarChartOptions = useCallback(
    (options: KwelloBarChartOptions) => {
      const newOptions = Object.keys(options).reduce((acc: KwelloBarChartOptions, key: string) => {
        // Create a typed key to avoid type errors within the references
        const typedKey = key as keyof KwelloBarChartOptions;

        // Get the current value of the option
        const currentValue = localWidgetOptions.kwelloBarChartOptions?.[typedKey];

        // Turn the new value into an array if it isn't already one ready for checking current value
        const newValue = (Array.isArray(options[typedKey])
          ? options[typedKey]
          : [options[typedKey]]) as unknown as any[];

        // If the current value is the same as one of the new values, keep it as is.
        // Use of any here is because TS thinks currentValue can be an array, but we're specifically using strings in this instance
        if (newValue.includes(currentValue as any)) {
          acc[typedKey] = currentValue as any;
          return acc;
        }

        // If the current value is not the same as one of the new values, add the first value of the new value to the accumulator
        acc[typedKey] = (newValue as any)[0];

        return acc;
      }, {});

      updateWidgetOptions({ kwelloBarChartOptions: newOptions });
    },
    [updateWidgetOptions, localWidgetOptions.kwelloBarChartOptions]
  );

  const updateKwelloLineChartOptions = useCallback(
    (options: KwelloLineChartOptions) => {
      updateWidgetOptions({ kwelloLineChartOptions: options });
    },
    [updateWidgetOptions]
  );

  const toggleAllIncluded = useCallback(() => {
    updateWidgetOptions({ allIncluded: !localWidgetOptions.allIncluded });
  }, [updateWidgetOptions, localWidgetOptions.allIncluded]);

  const resetWidgetOptions = useCallback(() => {
    if (optionsKey) {
      void deleteOptions(optionsKey);
    }

    setLocalWidgetOptions({
      allIncluded: false,
      groupBy: undefined,
      kwelloBarChartOptions: {
        ...DEFAULT_BARCHART_OPTIONS,
        ...barChartOptions,
      },
      kwelloLineChartOptions: {
        ...DEFAULT_LINECHART_OPTIONS,
        ...lineChartOptions,
      },
    });
  }, [optionsKey, barChartOptions, lineChartOptions, deleteOptions]);

  const isDirty = useMemo(() => {
    // Check if BarChartOptions are dirty
    if (localWidgetOptions?.kwelloBarChartOptions) {
      const baseBarChartOptions = {
        ...DEFAULT_BARCHART_OPTIONS,
        ...barChartOptions,
      };

      if (!equal(localWidgetOptions.kwelloBarChartOptions, baseBarChartOptions)) {
        return true;
      }
    }

    // Check if LineChartOptions are dirty
    if (localWidgetOptions?.kwelloLineChartOptions) {
      const baseLineChartOptions = {
        ...DEFAULT_LINECHART_OPTIONS,
        ...lineChartOptions,
      };

      if (!equal(localWidgetOptions.kwelloLineChartOptions, baseLineChartOptions)) {
        return true;
      }
    }

    // Check if GroupBy is dirty
    if (localWidgetOptions?.groupBy && localWidgetOptions.groupBy !== groupOptions[0]?.memberName) {
      return true;
    }

    // Check if AllIncluded is dirty
    if (localWidgetOptions?.allIncluded !== false) {
      return true;
    }

    return false;
  }, [
    localWidgetOptions.kwelloBarChartOptions,
    localWidgetOptions.kwelloLineChartOptions,
    localWidgetOptions.groupBy,
    localWidgetOptions?.allIncluded,
    groupOptions,
    barChartOptions,
    lineChartOptions,
  ]);

  const value: WidgetOptionsContext = useMemo(
    () => ({
      storedOptionsKey: optionsKey,
      groupBy: localWidgetOptions.groupBy,
      kwelloBarChartOptions: localWidgetOptions.kwelloBarChartOptions,
      kwelloLineChartOptions: localWidgetOptions.kwelloLineChartOptions,
      toggleAllIncluded,
      updateGroupBy,
      updateKwelloBarChartOptions,
      updateKwelloLineChartOptions,
      resetWidgetOptions,
      allIncluded: localWidgetOptions.allIncluded,
      activeTabId: localWidgetOptions.activeTabId,
      updateActiveTabId,
      isDirty,
    }),
    [
      localWidgetOptions,
      updateGroupBy,
      updateKwelloBarChartOptions,
      updateKwelloLineChartOptions,
      toggleAllIncluded,
      optionsKey,
      resetWidgetOptions,
      updateActiveTabId,
      isDirty,
    ]
  );

  return <WidgetOptions.Provider value={value} {...props} />;
};

const useWidgetOptionsContext = () => useContext(WidgetOptions);

export { WidgetOptionsProvider, useWidgetOptionsContext };
